import React, { useCallback, useEffect, useState } from 'react';
import { MdDirectionsBoat, MdLocationOn, MdRefresh, MdSpeed, MdLocalGasStation } from 'react-icons/md';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';

// Tab "Testing" di detail task: posisi & RPM/bahan bakar dari controller yg terpasang di kapal task
// (products dgn GpsGate Application ID + serial number), lewat backend /gps-tracking/vessels/:id —
// token GpsGate hanya di backend.

const BoatIcon = MdDirectionsBoat as any;
const PinIcon = MdLocationOn as any;
const RefreshIcon = MdRefresh as any;
const SpeedIcon = MdSpeed as any;
const FuelIcon = MdLocalGasStation as any;

interface VesselVariable { name: string; description: string | null; unit: string | null; value: any; time: string | null }
interface VesselPosition {
  product_id: number;
  product_name: string | null;
  serial: string;
  application_id: number;
  found: boolean;
  // ok | device_not_registered | token_invalid | gpsgate_error
  status: string;
  name?: string;
  username?: string;
  latitude?: number | null;
  longitude?: number | null;
  speed_knots?: number;
  heading?: number | null;
  position_time?: string | null;
  transport?: string | null;
  device_name?: string | null;
  variables?: VesselVariable[];
}

// Backend hanya mengirim variabel RPM & bahan bakar (flow / consumption / return), penamaan
// bernomor (EngineRPM1, Fuel_Flow1, Fuel_Engine1 = konsumsi) atau PS/SB (RPM_PS, Fuel_Flow_SB)
const RPM_RE = /^(EngineRPM\d+|RPM_(PS|SB))$/;
const FUEL_RE = /^(Fuel_(Engine|Flow|Return)\d+|Fuel_(Consumption|Flow|Return)_(PS|SB))$/;
// Urutan bahan bakar: konsumsi, flow, return — tiap jenis berpasangan (1/2 atau PS/SB)
const FUEL_ORDER = ['Consumption', 'Engine', 'Flow', 'Return'];
const fuelSortKey = (name: string) => {
  const kind = FUEL_ORDER.findIndex((k) => name.startsWith(`Fuel_${k}`));
  return `${kind < 0 ? 9 : kind}-${name}`;
};
const STALE_HOURS = 6;

const fmtTime = (iso: string | null, locale: string) =>
  iso ? new Date(iso).toLocaleString(locale, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

// Umur data dalam menit/jam/hari (untuk tahu data masih segar atau tidak)
const ago = (iso: string | null, td: any) => {
  if (!iso) return '';
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return (td?.gpsMinutesAgo || '{n} min ago').replace('{n}', String(min));
  const h = Math.round(min / 60);
  if (h < 48) return (td?.gpsHoursAgo || '{n} h ago').replace('{n}', String(h));
  return (td?.gpsDaysAgo || '{n} d ago').replace('{n}', String(Math.round(h / 24)));
};
const AUTO_REFRESH_SEC = 30;
const AUTO_REFRESH_KEY = 'vesselPositions.autoRefresh';
const isStale = (iso: string | null) => !!iso && Date.now() - new Date(iso).getTime() > STALE_HOURS * 3600000;

export const VesselPositionsTab: React.FC<{ vesselId: number }> = ({ vesselId }) => {
  const { t, language } = useLanguage();
  const td = t.taskDetails as any;
  const locale = language === 'id' ? 'id-ID' : 'en-GB';
  const [rows, setRows] = useState<VesselPosition[]>([]);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Masalah tingkat kapal dari backend: no_serial | no_application_id
  const [issue, setIssue] = useState<string | null>(null);

  // silent: refresh otomatis di latar — data lama tetap tampil (tanpa indikator loading)
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError('');
    try {
      const res = await axiosInstance.get(`/gps-tracking/vessels/${vesselId}`);
      setRows(res.data.rows || []);
      setIssue(res.data.issue || null);
      setFetchedAt(res.data.fetched_at || null);
      setError('');
    } catch (e: any) {
      if (!silent) setError(e.response?.data?.message || td?.gpsLoadFailed || 'Failed to load data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [vesselId, td?.gpsLoadFailed]);

  useEffect(() => { load(); }, [load]);

  // Auto refresh tiap AUTO_REFRESH_SEC detik — pilihan diingat per perangkat; berhenti saat
  // aplikasi/tab di latar belakang supaya tidak memanggil API GpsGate tanpa ada yg melihat
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    try { return localStorage.getItem(AUTO_REFRESH_KEY) === '1'; } catch { return false; }
  });
  const toggleAutoRefresh = () => {
    setAutoRefresh((prev) => {
      try { localStorage.setItem(AUTO_REFRESH_KEY, prev ? '0' : '1'); } catch { /* cukup di state */ }
      return !prev;
    });
  };
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, AUTO_REFRESH_SEC * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const issueMessage = (code: string | null) =>
    code === 'no_application_id' ? (td?.gpsNoApplicationId || 'Application ID is not registered yet, please contact the administrator')
      : (td?.gpsNoSerial || 'Serial Number is not registered yet, please contact the administrator');
  const statusMessage = (code: string) =>
    code === 'token_invalid' ? (td?.gpsTokenInvalid || 'Token is no longer valid')
      : code === 'device_not_registered' ? (td?.gpsDeviceNotRegistered || 'Device is not registered in the matching application')
      : (td?.gpsLoadFailed || 'Failed to load data');

  const card: React.CSSProperties = { background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 14, marginBottom: 12 };
  const label: React.CSSProperties = { fontSize: 11, color: '#6B7280' };
  const value: React.CSSProperties = { fontSize: 13, color: '#111827', fontWeight: 500 };
  const sectionTitle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#111827', margin: '4px 0 8px' };

  // Angka dari API dgn maksimal 5 digit di belakang koma (nol di belakang tidak ditambahkan) + pemisah ribuan
  const fmtRaw = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 5 });
  const fmtValue = (v: VesselVariable) => {
    if (typeof v.value === 'number') return `${fmtRaw(v.value)}${v.unit ? ` ${v.unit}` : ''}`;
    return String(v.value ?? '-');
  };

  // Label RPM = nama variabel apa adanya (RPM_PS, RPM_SB); khusus EngineRPM1 → "Engine 1"
  const rpmLabel = (name: string) => {
    const n = name.match(/^EngineRPM(\d+)$/);
    return n ? (td?.gpsEngineN || 'Engine {n}').replace('{n}', n[1]) : name;
  };

  const renderVariableGrid = (vars: VesselVariable[]) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {vars.map((v) => (
        <div key={v.name} style={{ background: '#F9FAFB', borderRadius: 8, padding: '8px 10px' }}>
          <div style={label} title={v.description || undefined}>{v.name}</div>
          <div style={{ ...value, color: isStale(v.time) ? '#9CA3AF' : '#111827' }}>{fmtValue(v)}</div>
        </div>
      ))}
    </div>
  );

  const renderEngine = (vars: VesselVariable[]) => {
    const rpm = vars.filter((v) => RPM_RE.test(v.name));
    const fuel = vars.filter((v) => FUEL_RE.test(v.name)).sort((a, b) => fuelSortKey(a.name).localeCompare(fuelSortKey(b.name), undefined, { numeric: true }));
    const latest = vars.reduce<string | null>((m, v) => (v.time && (!m || v.time > m) ? v.time : m), null);
    if (!vars.length) return <div style={{ ...label, marginTop: 8 }}>{td?.gpsNoEngineData || 'No engine data from this device'}</div>;
    return (
      <div style={{ marginTop: 12, borderTop: '1px solid #F3F4F6', paddingTop: 12 }}>
        {latest && (
          <div style={{ ...label, marginBottom: 8, color: isStale(latest) ? '#B45309' : '#6B7280' }}>
            {(td?.gpsEngineUpdated || 'Engine data {time}').replace('{time}', `${fmtTime(latest, locale)} · ${ago(latest, td)}`)}
            {isStale(latest) ? ` · ${td?.gpsStale || 'not updated recently'}` : ''}
          </div>
        )}
        {rpm.length > 0 && (
          <>
            <div style={sectionTitle}><SpeedIcon size={16} color='#2563EB' /> {td?.gpsRpmTitle || 'Engine RPM'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(rpm.length, 2)}, 1fr)`, gap: 8, marginBottom: 12 }}>
              {rpm.map((v) => {
                const running = Number(v.value) > 0;
                return (
                  <div key={v.name} style={{ borderRadius: 10, padding: '10px 12px', background: running ? '#EFF6FF' : '#F9FAFB', border: `1px solid ${running ? '#BFDBFE' : '#E5E7EB'}` }}>
                    <div style={label}>{rpmLabel(v.name)}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: running ? '#1D4ED8' : '#9CA3AF', lineHeight: 1.2 }}>
                      {typeof v.value === 'number' ? fmtRaw(v.value) : String(v.value ?? '-')} <span style={{ fontSize: 12, fontWeight: 500 }}>RPM</span>
                    </div>
                    <div style={label}>{running ? (td?.gpsEngineRunning || 'Running') : (td?.gpsEngineOff || 'Off')}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {fuel.length > 0 && (
          <>
            <div style={sectionTitle}><FuelIcon size={16} color='#D97706' /> {td?.gpsFuelTitle || 'Fuel'}</div>
            {renderVariableGrid(fuel)}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ paddingBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{td?.gpsTitle || 'Vessel positions'}</div>
          {fetchedAt && <div style={label}>{(td?.gpsUpdatedAt || 'Updated {time}').replace('{time}', fmtTime(fetchedAt, locale))}</div>}
        </div>
        <button type='button' onClick={() => load()} disabled={loading} aria-label={td?.gpsRefresh || 'Refresh'}
          style={{ display: 'flex', alignItems: 'center', gap: 4, border: '1px solid #E5E7EB', background: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 13, color: '#374151', opacity: loading ? 0.6 : 1 }}>
          <RefreshIcon size={16} /> {td?.gpsRefresh || 'Refresh'}
        </button>
      </div>

      {/* Toggle auto refresh */}
      <div onClick={toggleAutoRefresh} role='switch' aria-checked={autoRefresh}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: -4, marginBottom: 12, cursor: 'pointer' }}>
        <span style={{ fontSize: 12, color: autoRefresh ? '#1D4ED8' : '#6B7280' }}>
          {(td?.gpsAutoRefresh || 'Auto refresh every {n}s').replace('{n}', String(AUTO_REFRESH_SEC))}
        </span>
        <span style={{ width: 34, height: 20, borderRadius: 10, background: autoRefresh ? '#2563EB' : '#D1D5DB', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
          <span style={{ position: 'absolute', top: 2, left: autoRefresh ? 16 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
        </span>
      </div>

      {error && <div style={{ ...card, borderColor: '#FCA5A5', background: '#FEF2F2', color: '#B91C1C', fontSize: 13 }}>{error}</div>}

      {loading && !rows.length ? (
        [0, 1].map((i) => <div key={i} style={{ ...card, height: 140, background: '#F3F4F6', border: 'none' }} />)
      ) : !error && rows.length === 0 ? (
        <div style={{ ...card, borderColor: '#FDE68A', background: '#FFFBEB', color: '#92400E', fontSize: 13, fontWeight: 600 }}>
          {issueMessage(issue)}
        </div>
      ) : rows.map((v) => {
        const deviceInfo = `${v.product_name || '-'} · SN ${v.serial} · App ${v.application_id}`;
        if (!v.found) {
          return (
            <div key={v.product_id} style={{ ...card, borderColor: '#FDE68A', background: '#FFFBEB' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>{statusMessage(v.status)}</div>
              <div style={{ ...label, marginTop: 4 }}>{deviceInfo}</div>
            </div>
          );
        }
        const hasPos = v.latitude != null && v.longitude != null;
        const speed = v.speed_knots ?? 0;
        const moving = speed >= 0.5;
        return (
          <div key={v.product_id} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 18, background: moving ? '#DCFCE7' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BoatIcon size={20} color={moving ? '#16A34A' : '#6B7280'} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{v.name}</div>
                <div style={label}>{v.username}{v.transport ? ` · ${v.transport}` : ''}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 10, padding: '2px 8px', background: moving ? '#DCFCE7' : '#F3F4F6', color: moving ? '#15803D' : '#4B5563' }}>
                {moving ? (td?.gpsMoving || 'Moving') : (td?.gpsStopped || 'Stopped')}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
              <div>
                <div style={label}>{td?.gpsSpeed || 'Speed'}</div>
                <div style={value}>{fmtRaw(speed)} kn{v.heading != null ? ` · ${fmtRaw(v.heading)}°` : ''}</div>
              </div>
              <div>
                <div style={label}>{td?.gpsPositionTime || 'Position time'}</div>
                <div style={value}>{fmtTime(v.position_time ?? null, locale)}</div>
                <div style={label}>{ago(v.position_time ?? null, td)}</div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={label}>{td?.gpsCoordinates || 'Coordinates'}</div>
                {hasPos ? (
                  <a href={`https://www.google.com/maps?q=${v.latitude},${v.longitude}`} target='_blank' rel='noreferrer'
                    style={{ ...value, display: 'inline-flex', alignItems: 'center', gap: 4, color: '#2563EB', textDecoration: 'none' }}>
                    <PinIcon size={15} /> {Number(v.latitude!.toFixed(5))}, {Number(v.longitude!.toFixed(5))}
                  </a>
                ) : <div style={value}>-</div>}
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={label}>{td?.gpsDevice || 'Device'}</div>
                <div style={{ fontSize: 12, color: '#374151' }}>{deviceInfo}</div>
                {v.device_name && <div style={label}>{v.device_name}</div>}
              </div>
            </div>

            {v.variables && renderEngine(v.variables)}
          </div>
        );
      })}
    </div>
  );
};
